import { isDraft, isHidden, isPublished } from 'common/lib/helpers/adStatusHelpers';
import * as _ from 'lodash';
import { useDataContext } from 'common/lib/helpers/DataContext';
import { getQueryParamsAsObject } from 'common/lib/helpers/UrlUtils';
import { UNIT_APARTMENT, URL_PARAMS } from './constants';
import { applyUrlParamFilters } from './unitSearchFilter';

export const getUnits = (orderBy = null, ignoreSearchParamFilters = false, includeHiddenUnits = true) => {
    const { units, adStatus: projectAdStatus } = useDataContext();
    const urlParams = getQueryParamsAsObject(window.location.search);
    return getOrderedAndFilteredUnits(
        units,
        orderBy,
        urlParams,
        ignoreSearchParamFilters,
        projectAdStatus,
        includeHiddenUnits
    );
};

/**
 * @param units PTS-Unit ads
 * @param orderBy decide order of units
 * @param urlParamsAsObject url params containing search filter params and/or preview flag
 * @param ignoreSearchParamFilters if true, then search filters are not applied
 * @param projectAdStatus ad_status of pts-master ad (string)
 * @param includeHiddenUnits whether or not to include ads with ad_status active_but_hidden(52)
 * @returns {*[]|{userFilteredUnits, units, isUnitsHiddenBySearchFilters: boolean}}
 * units: Units with ad_status allowed on the object page. Based on pts-master ad_status and preview flag.
 * userFilteredUnits: units after applying search param filters.
 * isUnitsHiddenBySearchFilters: true if ads are hidden by search param filters.
 */
// eslint-disable-next-line no-underscore-dangle
export const getOrderedAndFilteredUnits = (
    units,
    orderBy,
    urlParamsAsObject,
    ignoreSearchParamFilters,
    projectAdStatus,
    includeHiddenUnits
) => {
    if (!units) return [];
    const unitOrderingResult = orderBy != null ? orderUnits(orderBy, units) : units;
    const unitStatusFilterResult = applyUnitStatusFilter(
        unitOrderingResult,
        urlParamsAsObject,
        projectAdStatus,
        includeHiddenUnits
    );
    const unitSearchParamFilterResult = applySearchParamFilter(
        unitStatusFilterResult,
        projectAdStatus,
        ignoreSearchParamFilters,
        urlParamsAsObject
    );

    return {
        units: unitStatusFilterResult,
        userFilteredUnits: unitSearchParamFilterResult,
        isUnitsHiddenBySearchFilters: unitSearchParamFilterResult < unitStatusFilterResult,
    };
};

const orderUnits = (orderBy, units) =>
    orderBy !== UNIT_APARTMENT.accessor
        ? _.orderBy(units, [orderBy], ['asc'])
        : units.sort((a, b) => {
              if (!a.apartmentNumber) {
                  return -1;
              }
              if (!b.apartmentNumber) {
                  return +1;
              }
              return a.apartmentNumber.localeCompare(b.apartmentNumber, 'no-nb', {
                  numeric: true,
                  sensitivity: 'base',
              });
          });

// Units that can be presented in the object page based on preview flag and master ad status
const applyUnitStatusFilter = (units, queryParamsAsObject, projectAdStatus, includeHiddenUnits) => {
    const unitsAfterPreviewFilter = units.filter(unit => previewFilter(unit, queryParamsAsObject, projectAdStatus));
    return includeHiddenUnits
        ? unitsAfterPreviewFilter
        : unitsAfterPreviewFilter.filter(unit => !isHidden(unit.adStatus));
};

const previewFilter = (unit, params, masterStatus) => {
    const isPreview = params[URL_PARAMS.preview] === '1';
    if (isPreview || isDraft(masterStatus)) return true;
    return isPublished(unit.adStatus);
};

// Units to display after applying url param filters from search
const applySearchParamFilter = (units, projectAdStatus, showAll, queryParamsAsObject) => {
    return showAll ? units : applyUrlParamFilters(units, queryParamsAsObject);
};
